import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { MagnifyingGlass, Trash, PencilSimple, X, Eye, WhatsappLogo, ArrowSquareOut } from "@phosphor-icons/react";
import { openWhatsapp } from "../utils/waOpener";

const CRMContacts = ({ user, logout }) => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [newContact, setNewContact] = useState({
    name: "", email: "", phone: "", company: "", position: "", tags: "", notes: "", assigned_to: user?.name || ""
  });
  const [editData, setEditData] = useState({});

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || ""
      });
      const response = await api.get(`${API}/crm/contacts?${params.toString()}`);
      setContacts(response.data);
    } catch (error) { toast.error("Failed to fetch contacts"); }
    finally { setLoading(false); }
  }, [user?.role, user?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const sendWhatsApp = async (contact) => {
    if (!contact.phone) { toast.error("No phone on contact"); return; }
    const message = `Hi ${contact.name}, this is ${user?.name} from SwifTech India. How can I help you today?`;
    const result = await openWhatsapp(contact.phone, message);
    try {
      await axios.post(`${API}/whatsapp/log`, {
        contact_id: contact.contact_id, contact_name: contact.name,
        phone: result.phone || contact.phone, message,
        sent_by: user?.user_id || "", sent_by_name: user?.name || "",
        kind: "direct"
      });
      toast.success("WhatsApp opened — logged to CRM");
    } catch (err) { console.debug("WhatsApp log skipped:", err); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${API}/crm/contacts`, {
        ...newContact,
        tags: newContact.tags.split(',').map(t => t.trim()).filter(t => t)
      });
      toast.success("Contact added!");
      setShowAdd(false);
      setNewContact({ name: "", email: "", phone: "", company: "", position: "", tags: "", notes: "", assigned_to: user?.name || "" });
      fetchContacts();
    } catch (error) { toast.error("Failed to add contact"); }
  };

  const handleUpdate = async (contactId) => {
    try {
      const updates = {};
      if (editData.name) updates.name = editData.name;
      if (editData.phone !== undefined) updates.phone = editData.phone;
      if (editData.company !== undefined) updates.company = editData.company;
      if (editData.position !== undefined) updates.position = editData.position;
      if (editData.notes !== undefined) updates.notes = editData.notes;
      if (editData.tags !== undefined) updates.tags = editData.tags.split(',').map(t => t.trim()).filter(t => t);

      await api.put(`${API}/crm/contacts/${contactId}`, updates);
      toast.success("Contact updated!");
      setEditingId(null);
      fetchContacts();
    } catch (error) { toast.error("Failed to update contact"); }
  };

  const handleDelete = async (contactId) => {
    try {
      await api.delete(`${API}/crm/contacts/${contactId}`);
      toast.success("Contact deleted!");
      fetchContacts();
    } catch (error) { toast.error("Failed to delete contact"); }
  };

  const filtered = contacts.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    c.company.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="page-header-sticky">
          <div className="px-8 py-5 flex justify-between items-start">
            <div>
              <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Contacts</h1><HelpButton moduleKey="customer" /></div>
              <p className="text-base leading-relaxed text-slate-700">Manage customer and prospect contacts</p>
            </div>
            <button data-testid="add-contact-btn" onClick={() => setShowAdd(!showAdd)}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300">
              {showAdd ? 'Cancel' : 'Add Contact'}
            </button>
          </div>
        </div>
        <div className="p-8 pt-6">
          {showAdd && (
            <div data-testid="add-contact-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>New Contact</h3>
              <form onSubmit={handleAdd} className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Name</label>
                  <input data-testid="contact-name-input" type="text" value={newContact.name} onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" required />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Email</label>
                  <input data-testid="contact-email-input" type="email" value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" required />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Phone</label>
                  <input data-testid="contact-phone-input" type="tel" value={newContact.phone} onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Company</label>
                  <input data-testid="contact-company-input" type="text" value={newContact.company} onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Position</label>
                  <input data-testid="contact-position-input" type="text" value={newContact.position} onChange={(e) => setNewContact({...newContact, position: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Tags (comma-separated)</label>
                  <input data-testid="contact-tags-input" type="text" value={newContact.tags} onChange={(e) => setNewContact({...newContact, tags: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" placeholder="VIP, Enterprise, Hot Lead" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Notes</label>
                  <textarea data-testid="contact-notes-input" value={newContact.notes} onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" rows="3" />
                </div>
                <div className="col-span-2">
                  <button data-testid="submit-contact-btn" type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300">
                    Add Contact
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Search */}
          <div className="mb-6 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="contact-search-input" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts by name, email, or company..."
              className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 pl-10 w-full outline-none" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <div data-testid="contacts-table" className="bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-y border-slate-200">
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Name</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Email</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Company</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Phone</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Tags</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((contact, index) => (
                    <tr key={contact.contact_id} data-testid={`contact-row-${index}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-300">
                      {editingId === contact.contact_id ? (
                        <>
                          <td className="p-4"><input type="text" value={editData.name || ""} onChange={e => setEditData({...editData, name: e.target.value})} className="rounded-none border border-slate-300 p-2 w-full text-sm outline-none" /></td>
                          <td className="p-4 text-sm text-slate-600">{contact.email}</td>
                          <td className="p-4"><input type="text" value={editData.company || ""} onChange={e => setEditData({...editData, company: e.target.value})} className="rounded-none border border-slate-300 p-2 w-full text-sm outline-none" /></td>
                          <td className="p-4"><input type="text" value={editData.phone || ""} onChange={e => setEditData({...editData, phone: e.target.value})} className="rounded-none border border-slate-300 p-2 w-full text-sm outline-none" /></td>
                          <td className="p-4"><input type="text" value={editData.tags || ""} onChange={e => setEditData({...editData, tags: e.target.value})} className="rounded-none border border-slate-300 p-2 w-full text-sm outline-none" /></td>
                          <td className="p-4 flex gap-2">
                            <button data-testid={`save-contact-${index}`} onClick={() => handleUpdate(contact.contact_id)} className="rounded-none bg-[#10B981] text-white px-3 py-1 text-xs font-bold">Save</button>
                            <button onClick={() => setEditingId(null)} className="rounded-none bg-slate-200 text-slate-700 px-3 py-1 text-xs font-bold"><X size={14} /></button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-4 font-medium">{contact.name}</td>
                          <td className="p-4 text-sm text-slate-600">{contact.email}</td>
                          <td className="p-4 text-sm text-slate-600">{contact.company || '-'}</td>
                          <td className="p-4 text-sm text-slate-600">{contact.phone || '-'}</td>
                          <td className="p-4">
                            <div className="flex gap-1 flex-wrap">
                              {contact.tags?.map((tag, i) => (
                                <span key={i} className="rounded-none px-2 py-0.5 text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">{tag}</span>
                              ))}
                            </div>
                          </td>
                          <td className="p-4 flex gap-2">
                            <button data-testid={`view-360-${index}`} onClick={() => navigate(`/crm/contacts/${contact.contact_id}/360`)}
                              className="rounded-none bg-blue-50 text-[#002FA7] hover:bg-blue-100 p-2 transition-colors duration-300" title="360 View"><Eye size={16} /></button>
                            <a
                              href={`/crm/contacts/${contact.contact_id}/360`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`contact-open-tab-${index}`}
                              title="Open in new tab"
                              onClick={(e) => e.stopPropagation()}
                              className="rounded-none bg-slate-50 text-slate-600 hover:bg-slate-100 p-2 transition-colors duration-300 inline-flex items-center"
                            >
                              <ArrowSquareOut size={16} weight="bold" />
                            </a>
                            {contact.phone && (
                              <button data-testid={`whatsapp-${index}`} onClick={() => sendWhatsApp(contact)}
                                className="rounded-none bg-green-50 text-[#25D366] hover:bg-green-100 p-2 transition-colors duration-300" title="WhatsApp"><WhatsappLogo size={16} weight="fill" /></button>
                            )}
                            <button data-testid={`edit-contact-${index}`} onClick={() => { setEditingId(contact.contact_id); setEditData({ name: contact.name, phone: contact.phone, company: contact.company, position: contact.position, tags: contact.tags?.join(', '), notes: contact.notes }); }}
                              className="rounded-none bg-slate-100 text-slate-700 hover:bg-slate-200 p-2 transition-colors duration-300"><PencilSimple size={16} /></button>
                            <button data-testid={`delete-contact-${index}`} onClick={() => handleDelete(contact.contact_id)}
                              className="rounded-none bg-red-50 text-red-700 hover:bg-red-100 p-2 transition-colors duration-300"><Trash size={16} /></button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-12 text-slate-500">No contacts found</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMContacts;
